import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { initialPhoneState, initialPhoneExtension } from "./phone.types";
import { LoadState } from "../../constants/enums";
import {
  fetchPhoneExtensions,
  fetchPhoneExtensionById,
  fetchPhoneExtensionByNumber,
  createPhoneExtension,
  updatePhoneExtension,
  deletePhoneExtension,
  fetchPhoneDids,
  createPhoneDid,
  fetchPhoneCdrs,
  fetchPhoneQueues,
  fetchPhoneBooks,
  createPhoneBook,
  fetchPhoneSimpleCdrs,
  fetchPhoneCdrAiAnalysis,
  initiateClickToCall,
  fetchPhoneIvrs,
  createPhoneIvr,
  fetchPhoneVoicemails,
  fetchPhoneMediaFiles,
  fetchPhoneFeatureCodes,
  fetchPhoneBookEntries,
  fetchPhoneCustomDestinations,
  fetchPhoneHuntLists,
  fetchPhoneConditions,
  fetchMyPhoneExt,
} from "./phoneThunk";

const phoneSlice = createSlice({
  name: "phone",
  initialState: initialPhoneState,
  reducers: {
    clearPhoneExtensions: (state) => {
      state.extensions.list = [];
      state.extensions.loading = LoadState.NotLoaded;
    },
    clearSinglePhoneExtension: (state) => {
      state.extensions.extension = initialPhoneExtension;
      state.extensions.extensionLoading = LoadState.NotLoaded;
    },
    clearPhoneDids: (state) => {
      state.dids = { ...initialPhoneState.dids };
    },
    clearPhoneCdrs: (state) => {
      state.cdrs = { ...initialPhoneState.cdrs };
    },
    clearPhoneQueues: (state) => {
      state.queues = { ...initialPhoneState.queues };
    },
    clearPhoneBooks: (state) => {
      state.phoneBooks = { ...initialPhoneState.phoneBooks };
    },
    clearPhoneSimpleCdrs: (state) => {
      state.simpleCdrs = { ...initialPhoneState.simpleCdrs };
    },
    clearPhoneCdrAiAnalysis: (state) => {
      state.aiAnalysis = { ...initialPhoneState.aiAnalysis };
    },
    clearClickToCall: (state) => {
      state.clickToCall = { ...initialPhoneState.clickToCall };
    },
    clearPhoneIvrs: (state) => {
      state.ivrs = { ...initialPhoneState.ivrs };
    },
    clearPhoneVoicemails: (state) => {
      state.voicemails = { ...initialPhoneState.voicemails };
    },
    clearPhoneMediaFiles: (state) => {
      state.mediaFiles = { ...initialPhoneState.mediaFiles };
    },
    clearPhoneFeatureCodes: (state) => {
      state.featureCodes = { ...initialPhoneState.featureCodes };
    },
    clearPhoneBookEntries: (state) => {
      state.phoneBookEntries = { ...initialPhoneState.phoneBookEntries };
    },
    clearPhoneCustomDestinations: (state) => {
      state.customDestinations = { ...initialPhoneState.customDestinations };
    },
    clearPhoneHuntLists: (state) => {
      state.huntLists = { ...initialPhoneState.huntLists };
    },
    clearPhoneConditions: (state) => {
      state.conditions = { ...initialPhoneState.conditions };
    },
    setLiveCall: (
      state,
      action: PayloadAction<{ call_ref: string; extension: string; dest: string; started_ts: string }>
    ) => {
      state.liveCall = action.payload;
    },
    clearLiveCall: (state) => {
      state.liveCall = null;
    },
  },
  extraReducers: (builder) => {
    // Extensions List
    builder.addCase(fetchPhoneExtensions.pending, (state) => {
      state.extensions.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneExtensions.fulfilled, (state, action) => {
      state.extensions.list = action.payload.data;
      state.extensions.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneExtensions.rejected, (state) => {
      state.extensions.loading = LoadState.Failed;
      state.extensions.list = [];
    });

    // Extension By ID
    builder.addCase(fetchPhoneExtensionById.pending, (state) => {
      state.extensions.extensionLoading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneExtensionById.fulfilled, (state, action) => {
      state.extensions.extension = action.payload.data;
      state.extensions.extensionLoading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneExtensionById.rejected, (state) => {
      state.extensions.extensionLoading = LoadState.Failed;
    });

    // Extension By Number
    builder.addCase(fetchPhoneExtensionByNumber.pending, (state) => {
      state.extensions.extensionLoading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneExtensionByNumber.fulfilled, (state, action) => {
      state.extensions.extension = action.payload.data;
      state.extensions.extensionLoading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneExtensionByNumber.rejected, (state) => {
      state.extensions.extensionLoading = LoadState.Failed;
    });

    // Create Extension
    builder.addCase(createPhoneExtension.pending, (state) => {
      state.extensions.saveLoading = true;
    });
    builder.addCase(createPhoneExtension.fulfilled, (state) => {
      state.extensions.saveLoading = false;
    });
    builder.addCase(createPhoneExtension.rejected, (state) => {
      state.extensions.saveLoading = false;
    });

    // Update Extension
    builder.addCase(updatePhoneExtension.pending, (state) => {
      state.extensions.saveLoading = true;
    });
    builder.addCase(updatePhoneExtension.fulfilled, (state, action) => {
      state.extensions.saveLoading = false;
      state.extensions.extension = action.payload.data;
    });
    builder.addCase(updatePhoneExtension.rejected, (state) => {
      state.extensions.saveLoading = false;
    });

    // Delete Extension
    builder.addCase(deletePhoneExtension.pending, (state) => {
      state.extensions.saveLoading = true;
    });
    builder.addCase(deletePhoneExtension.fulfilled, (state) => {
      state.extensions.saveLoading = false;
    });
    builder.addCase(deletePhoneExtension.rejected, (state) => {
      state.extensions.saveLoading = false;
    });

    // DIDs
    builder.addCase(fetchPhoneDids.pending, (state) => {
      state.dids.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneDids.fulfilled, (state, action) => {
      state.dids.list = action.payload.data;
      state.dids.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneDids.rejected, (state) => {
      state.dids.loading = LoadState.Failed;
      state.dids.list = [];
    });

    // Create DID
    builder.addCase(createPhoneDid.pending, (state) => {
      state.dids.saveLoading = true;
    });
    builder.addCase(createPhoneDid.fulfilled, (state) => {
      state.dids.saveLoading = false;
    });
    builder.addCase(createPhoneDid.rejected, (state) => {
      state.dids.saveLoading = false;
    });

    // CDRs
    builder.addCase(fetchPhoneCdrs.pending, (state) => {
      state.cdrs.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneCdrs.fulfilled, (state, action) => {
      state.cdrs.list = [...action.payload.data].sort((a, b) => {
        return new Date(b.start).getTime() - new Date(a.start).getTime();
      });
      state.cdrs.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneCdrs.rejected, (state) => {
      state.cdrs.loading = LoadState.Failed;
      state.cdrs.list = [];
    });

    // Queues
    builder.addCase(fetchPhoneQueues.pending, (state) => {
      state.queues.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneQueues.fulfilled, (state, action) => {
      state.queues.list = action.payload.data;
      state.queues.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneQueues.rejected, (state) => {
      state.queues.loading = LoadState.Failed;
      state.queues.list = [];
    });

    // Phone Books
    builder.addCase(fetchPhoneBooks.pending, (state) => {
      state.phoneBooks.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneBooks.fulfilled, (state, action) => {
      state.phoneBooks.list = action.payload.data;
      state.phoneBooks.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneBooks.rejected, (state) => {
      state.phoneBooks.loading = LoadState.Failed;
      state.phoneBooks.list = [];
    });

    // Create Phone Book
    builder.addCase(createPhoneBook.pending, (state) => {
      state.phoneBooks.saveLoading = true;
    });
    builder.addCase(createPhoneBook.fulfilled, (state) => {
      state.phoneBooks.saveLoading = false;
    });
    builder.addCase(createPhoneBook.rejected, (state) => {
      state.phoneBooks.saveLoading = false;
    });

    // Simple CDRs
    builder.addCase(fetchPhoneSimpleCdrs.pending, (state) => {
      state.simpleCdrs.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneSimpleCdrs.fulfilled, (state, action) => {
      state.simpleCdrs.list = action.payload.data;
      state.simpleCdrs.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneSimpleCdrs.rejected, (state) => {
      state.simpleCdrs.loading = LoadState.Failed;
      state.simpleCdrs.list = [];
    });

    // CDR AI Analysis
    builder.addCase(fetchPhoneCdrAiAnalysis.pending, (state) => {
      state.aiAnalysis.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneCdrAiAnalysis.fulfilled, (state, action) => {
      state.aiAnalysis.data = action.payload.data;
      state.aiAnalysis.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneCdrAiAnalysis.rejected, (state) => {
      state.aiAnalysis.loading = LoadState.Failed;
      state.aiAnalysis.data = null;
    });

    // Click-to-Call
    builder.addCase(initiateClickToCall.pending, (state) => {
      state.clickToCall.loading = LoadState.InProgress;
      state.clickToCall.error = null;
    });
    builder.addCase(initiateClickToCall.fulfilled, (state) => {
      state.clickToCall.loading = LoadState.Loaded;
      state.clickToCall.error = null;
    });
    builder.addCase(initiateClickToCall.rejected, (state, action) => {
      state.clickToCall.loading = LoadState.Failed;
      state.clickToCall.error = action.payload?.message || "Call failed";
    });

    // IVRs
    builder.addCase(fetchPhoneIvrs.pending, (state) => {
      state.ivrs.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneIvrs.fulfilled, (state, action) => {
      state.ivrs.list = action.payload.data;
      state.ivrs.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneIvrs.rejected, (state) => {
      state.ivrs.loading = LoadState.Failed;
      state.ivrs.list = [];
    });

    // Create IVR
    builder.addCase(createPhoneIvr.pending, (state) => {
      state.ivrs.saveLoading = true;
    });
    builder.addCase(createPhoneIvr.fulfilled, (state) => {
      state.ivrs.saveLoading = false;
    });
    builder.addCase(createPhoneIvr.rejected, (state) => {
      state.ivrs.saveLoading = false;
    });

    // Voicemails
    builder.addCase(fetchPhoneVoicemails.pending, (state) => {
      state.voicemails.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneVoicemails.fulfilled, (state, action) => {
      state.voicemails.list = action.payload.data;
      state.voicemails.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneVoicemails.rejected, (state) => {
      state.voicemails.loading = LoadState.Failed;
      state.voicemails.list = [];
    });

    // Media Files
    builder.addCase(fetchPhoneMediaFiles.pending, (state) => {
      state.mediaFiles.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneMediaFiles.fulfilled, (state, action) => {
      state.mediaFiles.list = action.payload.data;
      state.mediaFiles.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneMediaFiles.rejected, (state) => {
      state.mediaFiles.loading = LoadState.Failed;
      state.mediaFiles.list = [];
    });

    // Feature Codes
    builder.addCase(fetchPhoneFeatureCodes.pending, (state) => {
      state.featureCodes.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneFeatureCodes.fulfilled, (state, action) => {
      state.featureCodes.list = action.payload.data;
      state.featureCodes.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneFeatureCodes.rejected, (state) => {
      state.featureCodes.loading = LoadState.Failed;
      state.featureCodes.list = [];
    });

    // Phone Book Entries
    builder.addCase(fetchPhoneBookEntries.pending, (state) => {
      state.phoneBookEntries.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneBookEntries.fulfilled, (state, action) => {
      state.phoneBookEntries.list = action.payload.data;
      state.phoneBookEntries.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneBookEntries.rejected, (state) => {
      state.phoneBookEntries.loading = LoadState.Failed;
      state.phoneBookEntries.list = [];
    });

    // Custom Destinations
    builder.addCase(fetchPhoneCustomDestinations.pending, (state) => {
      state.customDestinations.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneCustomDestinations.fulfilled, (state, action) => {
      state.customDestinations.list = action.payload.data;
      state.customDestinations.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneCustomDestinations.rejected, (state) => {
      state.customDestinations.loading = LoadState.Failed;
      state.customDestinations.list = [];
    });

    // Hunt Lists
    builder.addCase(fetchPhoneHuntLists.pending, (state) => {
      state.huntLists.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneHuntLists.fulfilled, (state, action) => {
      state.huntLists.list = action.payload.data;
      state.huntLists.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneHuntLists.rejected, (state) => {
      state.huntLists.loading = LoadState.Failed;
      state.huntLists.list = [];
    });

    // Conditions
    builder.addCase(fetchPhoneConditions.pending, (state) => {
      state.conditions.loading = LoadState.InProgress;
    });
    builder.addCase(fetchPhoneConditions.fulfilled, (state, action) => {
      state.conditions.list = action.payload.data;
      state.conditions.loading = LoadState.Loaded;
    });
    builder.addCase(fetchPhoneConditions.rejected, (state) => {
      state.conditions.loading = LoadState.Failed;
      state.conditions.list = [];
    });

    // My phone_ext (signed-in user's MirtaPBX extension).
    builder.addCase(fetchMyPhoneExt.pending, (state) => {
      state.me.loading = LoadState.InProgress;
    });
    builder.addCase(fetchMyPhoneExt.fulfilled, (state, action) => {
      state.me.phone_ext = action.payload.phone_ext;
      state.me.loading = LoadState.Loaded;
    });
    builder.addCase(fetchMyPhoneExt.rejected, (state) => {
      state.me.loading = LoadState.Failed;
      state.me.phone_ext = null;
    });
  },
});

export const { clearPhoneExtensions, clearSinglePhoneExtension, clearPhoneDids, clearPhoneCdrs, clearPhoneQueues, clearPhoneBooks, clearPhoneSimpleCdrs, clearPhoneCdrAiAnalysis, clearClickToCall, clearPhoneIvrs, clearPhoneVoicemails, clearPhoneMediaFiles, clearPhoneFeatureCodes, clearPhoneBookEntries, clearPhoneCustomDestinations, clearPhoneHuntLists, clearPhoneConditions, setLiveCall, clearLiveCall } = phoneSlice.actions;
export const phoneReducer = phoneSlice.reducer;

// Selector shortcut — call-site code reads the signed-in user's phone_ext
// through this instead of digging into state shape directly.
import type { IStoreState } from "../initialStoreState";
export const selectMyPhoneExt = (state: IStoreState): string | null =>
  state.phone?.me?.phone_ext || null;
